import React, { useState } from 'react';
import { Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack, Typography } from '@mui/material';
import { apiErrorMessage, launchApi } from '../api';
import { launchStatusText } from '../helpers';
import { launchColors } from '../launchTheme';
import { TemplateLaunchPreview, TemplateNameChange } from '../types';

const SHOWN = 6;

const changeText = (change: TemplateNameChange): string => {
  if (change.kind === 'phase') return `Fas: ${change.from} → ${change.to}`;
  if (change.kind === 'role') return `Roll för ${change.row}: ${change.from} → ${change.to}`;
  if (change.kind === 'order') return change.to ? `${change.row} flyttas före ${change.to}` : `${change.row} flyttas sist i fasen`;
  return `${change.from} → ${change.to}`;
};

const originText: Record<TemplateLaunchPreview['origin'], string> = {
  this: 'Skapad från den här mallen',
  other: 'Skapad från en annan mall',
  unknown: 'Skapad från en äldre mall',
};

const commandId = () => (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
  ? `template-sync-${crypto.randomUUID()}`
  : `template-sync-${Date.now()}`);

const TemplateLaunchSyncDialog: React.FC<{
  templateId: string;
  templateName: string;
  launches: TemplateLaunchPreview[];
  onClose: () => void;
  onDone: (notice: string | null, error: string | null) => void;
}> = ({ templateId, templateName, launches, onClose, onDone }) => {
  const [chosen, setChosen] = useState<string[]>(() => launches.filter((launch) => launch.origin !== 'other').map((launch) => launch.id));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (id: string, on: boolean) => setChosen((current) => (on ? [...current, id] : current.filter((item) => item !== id)));

  const apply = async () => {
    const targets = launches.filter((launch) => chosen.includes(launch.id)).map((launch) => ({ id: launch.id, expectedVersion: launch.expectedVersion }));
    setBusy(true);
    setError(null);
    try {
      const result = await launchApi.applyTemplateToLaunches(templateId, targets, commandId());
      const notice = result.updated.length === 1 ? '1 lansering är uppdaterad.' : `${result.updated.length} lanseringar är uppdaterade.`;
      const problems = [
        result.conflicts.length ? `${result.conflicts.length === 1 ? '1 lansering' : `${result.conflicts.length} lanseringar`} ändrades samtidigt av någon annan och uppdaterades inte. Spara mallen igen för att försöka på nytt.` : '',
        ...result.failed.map((item) => `${launches.find((launch) => launch.id === item.id)?.name || item.id}: ${item.message}`),
      ].filter(Boolean);
      onDone(result.updated.length ? notice : null, problems.length ? problems.join(' ') : null);
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'Lanseringarna kunde inte uppdateras.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Uppdatera lanseringar som redan finns?</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography sx={{ color: launchColors.textSecondary }}>
            {`Namn på rader och faser, roller och radernas ordning från ${templateName || 'mallen'} kan föras över. Datum, veckor, vikt och typ ändras inte i lanseringar som redan finns.`}
          </Typography>
          {launches.map((launch) => {
            const rest = launch.changes.length - SHOWN;
            return (
              <Box key={launch.id} sx={{ p: 1.5, border: `1px solid ${launchColors.divider}`, borderRadius: '12px' }}>
                <FormControlLabel
                  control={<Checkbox checked={chosen.includes(launch.id)} onChange={(event) => toggle(launch.id, event.target.checked)} />}
                  label={<Typography sx={{ fontWeight: 700, color: launchColors.navy }}>{launch.name}</Typography>}
                />
                <Typography sx={{ fontSize: 13, color: launchColors.textSecondary, mb: 0.5 }}>
                  {`${launchStatusText[launch.status]} · ${originText[launch.origin]}`}
                </Typography>
                <Stack component="ul" spacing={0.25} sx={{ m: 0, pl: 2.5 }}>
                  {launch.changes.slice(0, SHOWN).map((change) => (
                    <Typography component="li" key={`${change.kind}:${change.key}`} sx={{ fontSize: 14 }}>{changeText(change)}</Typography>
                  ))}
                  {rest > 0 && <Typography component="li" sx={{ fontSize: 14 }}>{`och ${rest} till`}</Typography>}
                </Stack>
                {launch.held.length > 0 && (
                  <Typography sx={{ fontSize: 13, color: launchColors.textSecondary, mt: 0.5 }}>
                    {`Rollen ändras inte på ${launch.held.length === 1 ? '1 beslut' : `${launch.held.length} beslut`} som redan har registrerade beslut.`}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>Inte nu</Button>
        <Button variant="contained" disabled={busy || chosen.length === 0} onClick={() => { void apply(); }}>
          {chosen.length === 1 ? 'Uppdatera 1 lansering' : `Uppdatera ${chosen.length} lanseringar`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateLaunchSyncDialog;
